import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { AgentDetailsBrickSettingRemoteDto, BrickSettingsService } from '../../../../../../../generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import {
   ChangeNumOfCallHistoryEntriesToShow,
   ChangeShowAvatar,
   ChangeShowCaller,
   ChangeShowCallHistory,
   ChangeShowGroups,
   ChangeShowSkills,
} from './agent-details-settings.action';

export interface AgentDetailsSettingStateModel extends IBrickSettingStateModel<AgentDetailsBrickSettingRemoteDto> {}

function getDefaultSpecificBrickSetting(): AgentDetailsBrickSettingRemoteDto {
   return {
      showAvatar: true,
      showCaller: true,
      showGroups: true,
      showSkills: true,
      showCallHistory: true,
      numOfCallHistoryEntriesToShow: 3,
      commonBrickSettingRemoteDto: null,
   };
}

@Injectable()
@State<AgentDetailsSettingStateModel>({
   name: 'agentDetailBrickSetting',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
export class AgentDetailsSettingState extends SpecificBrickSettingsStateBase<AgentDetailsBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([AgentDetailsSettingState, AgentDetailsSettingState.getSettingModelFn])
   public static getShowAvatar(state, getSettingModelFn: (state, brickInstanceId) => AgentDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showAvatar;
      };
   }

   @Selector([AgentDetailsSettingState, AgentDetailsSettingState.getSettingModelFn])
   public static getShowCaller(state, getSettingModelFn: (state, brickInstanceId) => AgentDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showCaller;
      };
   }

   @Selector([AgentDetailsSettingState, AgentDetailsSettingState.getSettingModelFn])
   public static getShowSkills(state, getSettingModelFn: (state, brickInstanceId) => AgentDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showSkills;
      };
   }

   @Selector([AgentDetailsSettingState, AgentDetailsSettingState.getSettingModelFn])
   public static getShowGroups(state, getSettingModelFn: (state, brickInstanceId) => AgentDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showGroups;
      };
   }

   @Selector([AgentDetailsSettingState, AgentDetailsSettingState.getSettingModelFn])
   public static getShowCallHistory(state, getSettingModelFn: (state, brickInstanceId) => AgentDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showCallHistory;
      };
   }

   @Selector([AgentDetailsSettingState])
   public static getMaxNumOfEntriesInCallHistory(state: AgentDetailsSettingStateModel): number {
      const brickSettings = _.isNil(state.specificBrickSettings.brickSettingsEditMode)
         ? state.specificBrickSettings.brickSettings
         : state.specificBrickSettings.brickSettingsEditMode;
      const settings: AgentDetailsBrickSettingRemoteDto[] = _.values(brickSettings);
      return (
         _(settings)
            .map((x) => x.numOfCallHistoryEntriesToShow)
            .max() ?? this.getSettingModelOrDefault(state, null).numOfCallHistoryEntriesToShow
      );
   }

   @Selector([AgentDetailsSettingState, AgentDetailsSettingState.getSettingModelFn])
   public static getNumOfCallHistoryEntriesToShow(state, getSettingModelFn: (state, brickInstanceId) => AgentDetailsBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId)?.numOfCallHistoryEntriesToShow;
      };
   }

   @Action(ChangeNumOfCallHistoryEntriesToShow)
   public changeNumOfCallHistoryEntriesToShow(ctx: StateContext<AgentDetailsSettingStateModel>, action: ChangeNumOfCallHistoryEntriesToShow) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = AgentDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.numOfCallHistoryEntriesToShow = action.numOfCallEntriesToShow;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowCaller)
   public changeShowCaller(ctx: StateContext<AgentDetailsSettingStateModel>, action: ChangeShowCaller) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = AgentDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showCaller = action.showCaller;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowSkills)
   public changeShowSkills(ctx: StateContext<AgentDetailsSettingStateModel>, action: ChangeShowSkills) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = AgentDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showSkills = action.showSkills;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowGroups)
   public changeShowGroups(ctx: StateContext<AgentDetailsSettingStateModel>, action: ChangeShowGroups) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = AgentDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showGroups = action.showGroups;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowCallHistory)
   public changeShowCallHistory(ctx: StateContext<AgentDetailsSettingStateModel>, action: ChangeShowCallHistory) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = AgentDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showCallHistory = action.showCallHistory;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowAvatar)
   public changeShowAvatar(ctx: StateContext<AgentDetailsSettingStateModel>, action: ChangeShowAvatar) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = AgentDetailsSettingState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showAvatar = action.showAvatar;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: AgentDetailsBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeAgentDetailsBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
